'use client';

import {
  BodyDefault,
  Button,
  Drawer,
  EmptyState,
  HeadingItem,
  IconButton,
  MetaDefault,
  Spinner,
  Tooltip,
  VirtualizedStack,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import { Cancel01Icon, InformationCircleIcon } from '@terra/ui-icons';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import {
  attachExecutionsToMcpDiscoveryRun,
  resolveMcpRunToolListExecutions,
  resolveMcpRunTopTraceExecution,
  type McpDiscoveryRun,
} from './mcp-discovery-run.util';
import { sortMcpDiscoveryToolExecutionsForDisplay } from './mcp-discovery-run-aggregate.util';
import {
  canRetryDiscoveryProbe,
  formatDiscoveryDuration,
  formatDiscoveryProbeFailureDetail,
  getAiDiscoveryMetadata,
  getDiscoveryFailureReason,
  getExecutionLabel,
  resolveDiscoveryProbeDisplayStatus,
} from './discovery.utils';
import { AiDiscoveryStatusBadge } from './AiDiscoveryStatusBadge';

type AiDiscoveryMcpRunDrawerProps = {
  run: McpDiscoveryRun | null;
  executions: AgentExecutionDto[];
  onClose: () => void;
  onOpenExecution: (executionId: string) => void;
  onRetryProbe?: (candidateId: string) => void;
};

export function AiDiscoveryMcpRunDrawer({
  run,
  executions,
  onClose,
  onOpenExecution,
  onRetryProbe,
}: AiDiscoveryMcpRunDrawerProps) {
  const t = useTranslations('discovery.aiApplication');
  const tCommon = useTranslations('common');
  const hydratedRun = useMemo(() => {
    if (!run) {
      return null;
    }

    return attachExecutionsToMcpDiscoveryRun({ run, executions });
  }, [executions, run]);
  const displayedExecutions = useMemo(() => {
    if (!hydratedRun) {
      return [];
    }

    const topTraceExecution = resolveMcpRunTopTraceExecution(hydratedRun);
    const toolExecutions = resolveMcpRunToolListExecutions({
      run: hydratedRun,
      topTraceExecution,
    });

    return toolExecutions.length > 0
      ? toolExecutions
      : sortMcpDiscoveryToolExecutionsForDisplay(hydratedRun.executions);
  }, [hydratedRun]);

  return (
    <Drawer.Root
      open={hydratedRun != null}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
    >
      <Drawer.Backdrop top="10" />
      <Drawer.Positioner w="480px" top="10" bottom="0" h="auto">
        <Drawer.Content
          display="flex"
          flexDirection="column"
          overflowX="hidden"
          h="full"
        >
          <Drawer.Header px="3" py="3">
            <HStack
              justifyContent="space-between"
              w="full"
              gap="2"
              alignItems="center"
            >
              <HStack gap="2" alignItems="center" minW="0" flex="1">
                <HeadingItem truncate>{t('mcpRunDetailTitle')}</HeadingItem>
                {hydratedRun ? (
                  <>
                    <AiDiscoveryStatusBadge status={hydratedRun.status} />
                    <MetaDefault color="fg.muted" truncate>
                      {t('mcpToolsProgress', {
                        verified: hydratedRun.verifiedProbeCount,
                        total: hydratedRun.probeCount,
                      })}
                      {' · '}
                      {formatDiscoveryDuration({
                        startedAt: hydratedRun.startedAt,
                        completedAt: hydratedRun.completedAt,
                        durationMs: hydratedRun.durationMs,
                      })}
                    </MetaDefault>
                  </>
                ) : null}
              </HStack>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={tCommon('close')}
                onClick={onClose}
                flexShrink={0}
              >
                <Cancel01Icon size={16} />
              </IconButton>
            </HStack>
          </Drawer.Header>
          <Drawer.Body px="3" py="3" flex="1" minH="0" overflowY="auto">
            {hydratedRun && displayedExecutions.length === 0 ? (
              hydratedRun.probeCount > 0 ? (
                <HStack w="full" justifyContent="center" py="8">
                  <Spinner size="sm" />
                  <MetaDefault color="fg.muted">
                    {tCommon('loading')}
                  </MetaDefault>
                </HStack>
              ) : (
                <EmptyState
                  title={t('emptyRun')}
                  description={
                    formatDiscoveryProbeFailureDetail(
                      hydratedRun.failureReason,
                      t,
                    ) ?? undefined
                  }
                  maxWidth="full"
                />
              )
            ) : null}
            {hydratedRun && displayedExecutions.length > 0 ? (
              <VirtualizedStack
                data={displayedExecutions}
                computeItemKey={({ item }) => item.id}
                renderItem={({ item: execution }) => {
                  const metadata = getAiDiscoveryMetadata(execution.metadata);
                  const candidateId = metadata?.candidateId;
                  const probeLabel = getExecutionLabel({ execution });
                  const failureDetail = formatDiscoveryProbeFailureDetail(
                    getDiscoveryFailureReason({
                      metadata,
                      runStatus: execution.status,
                    }),
                    t,
                  );
                  const canRetry =
                    onRetryProbe &&
                    candidateId &&
                    canRetryDiscoveryProbe({ execution });

                  return (
                    <HStack
                      w="full"
                      px="2"
                      py="2"
                      gap="2"
                      alignItems="center"
                      borderBottomWidth="1px"
                      borderColor="border.muted"
                      cursor="pointer"
                      _hover={{ bg: 'bg.subtle' }}
                      onClick={() => onOpenExecution(execution.id)}
                    >
                      <HStack gap="2" alignItems="center" minW="0" flex="1">
                        <AiDiscoveryStatusBadge
                          status={resolveDiscoveryProbeDisplayStatus({
                            execution,
                          })}
                        />
                        <Tooltip
                          title={probeLabel}
                          positioning={{
                            strategy: 'fixed',
                            placement: 'top',
                          }}
                        >
                          <Box minW="0" flex="1">
                            <BodyDefault truncate>{probeLabel}</BodyDefault>
                          </Box>
                        </Tooltip>
                      </HStack>
                      {failureDetail ? (
                        <HStack
                          flexShrink={0}
                          onClick={(event) => event.stopPropagation()}
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <Tooltip
                            title={failureDetail}
                            positioning={{ strategy: 'fixed' }}
                            maxW="360px"
                          >
                            <IconButton
                              size="xs"
                              variant="ghost"
                              color="fg.error"
                              aria-label={tCommon('error')}
                            >
                              <InformationCircleIcon size={14} />
                            </IconButton>
                          </Tooltip>
                        </HStack>
                      ) : null}
                      {canRetry ? (
                        <HStack
                          gap="1"
                          onClick={(event) => event.stopPropagation()}
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => {
                              onRetryProbe(candidateId);
                            }}
                          >
                            {t('retry')}
                          </Button>
                        </HStack>
                      ) : null}
                    </HStack>
                  );
                }}
              />
            ) : null}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
